import { useRef, useState, type DragEvent } from 'react'

interface DropZoneProps {
  onFiles: (files: File[]) => void
  accept?: string
  maxSizeMb?: number
  disabled?: boolean
  hint?: string
}

/** Dashed 0.5px border on background-input, per plan Section 12.6. */
export function DropZone({ onFiles, accept, maxSizeMb = 25, disabled, hint }: DropZoneProps) {
  const [over, setOver] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const handle = (files: FileList | null) => {
    if (!files || files.length === 0) return
    const list = Array.from(files)
    const tooBig = list.find((f) => f.size > maxSizeMb * 1024 * 1024)
    if (tooBig) {
      setError(`${tooBig.name} is larger than ${maxSizeMb} MB`)
      return
    }
    setError(null)
    onFiles(list)
  }

  const onDrop = (e: DragEvent) => {
    e.preventDefault()
    setOver(false)
    if (!disabled) handle(e.dataTransfer.files)
  }

  return (
    <div className="flex flex-col" style={{ gap: 8 }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setOver(true) }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
        className="w-full flex flex-col items-center justify-center font-mulish"
        style={{
          backgroundColor: '#1F2937',
          border: `0.5px dashed ${over ? '#85BEFF' : '#4B5563'}`,
          padding: '24px 20px', gap: 4, cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1, transition: 'background-color 0.15s ease',
        }}
      >
        <span className="text-white" style={{ fontSize: 12 }}>Drop files here, or click to choose</span>
        <span className="text-text-support" style={{ fontSize: 10 }}>
          {hint ?? `PDF, Word, Excel, CSV or images. Up to ${maxSizeMb} MB each.`}
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        hidden
        onChange={(e) => { handle(e.target.files); e.target.value = '' }}
      />
      {error && <span className="font-mulish" style={{ fontSize: 10, color: '#F05042' }}>{error}</span>}
    </div>
  )
}
